import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { colorOf } from "@/lib/shiftColors";
import ShiftScheduler from "@/components/app/ShiftScheduler";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ChevronLeft, ChevronRight, Loader2, Users, UserX, Sun, Sunset, Moon,
  CalendarClock, CalendarRange, LayoutGrid, ChevronRight as ArrowRight,
} from "lucide-react";

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayStr = () => fmtDate(new Date());
const yesterdayStr = () => { const d = new Date(); d.setDate(d.getDate() - 1); return fmtDate(d); };

function shiftIcon(start) {
  const h = parseInt((start || "").slice(0, 2), 10);
  if (Number.isNaN(h)) return CalendarClock;
  if (h >= 5 && h < 12) return Sun;
  if (h >= 12 && h < 18) return Sunset;
  return Moon;
}

const roleLabel = (r) => (r || "").replace(/_/g, " ");

export default function Shifts() {
  return (
    <div className="space-y-6 max-w-6xl" data-testid="shifts-page">
      <div>
        <span className="label-overline">Workforce</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Shifts</h1>
        <p className="text-sm text-zinc-500 mt-1">See who is covering each shift, and build the duty roster.</p>
      </div>

      <Tabs defaultValue="coverage">
        <TabsList data-testid="shifts-tabs">
          <TabsTrigger value="coverage" data-testid="tab-coverage"><LayoutGrid className="w-3.5 h-3.5 mr-1.5" />Coverage</TabsTrigger>
          <TabsTrigger value="roster" data-testid="tab-roster"><CalendarRange className="w-3.5 h-3.5 mr-1.5" />Roster builder</TabsTrigger>
        </TabsList>

        <TabsContent value="coverage" className="mt-5">
          <ShiftCoverage />
        </TabsContent>
        <TabsContent value="roster" className="mt-5">
          <ShiftScheduler />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ShiftCoverage() {
  const { selectedId } = useProperty();
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // { name, color, total, by_department }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/shifts/coverage?date=${date}${selectedId ? `&property_id=${selectedId}` : ""}`;
      const { data: d } = await api.get(url);
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date, selectedId]);

  useEffect(() => { load(); }, [load]);

  const shiftDay = (delta) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(fmtDate(d));
  };

  const dObj = new Date(date + "T00:00:00");
  const niceDate = dObj.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" });
  const tag = date === todayStr() ? "Today" : date === yesterdayStr() ? "Yesterday" : null;

  const kpis = data ? [
    { label: "On shift", value: data.totals.on_shift, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", num: "text-emerald-700", icon: Users },
    { label: "Shifts running", value: data.totals.shifts_covered, cls: "bg-blue-50 border-blue-200 text-blue-800", num: "text-blue-700", icon: CalendarClock },
    { label: "Unassigned", value: data.totals.unassigned, cls: "bg-amber-50 border-amber-200 text-amber-800", num: "text-amber-700", icon: UserX },
    { label: "Active staff", value: data.totals.active_staff, cls: "bg-zinc-50 border-zinc-200 text-zinc-700", num: "text-zinc-900", icon: Users },
  ] : [];

  return (
    <div className="space-y-5">
      {/* Date navigator */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-white border border-zinc-200 rounded-md p-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(-1)} data-testid="coverage-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="min-w-[180px] text-center">
            <div className="font-display text-lg font-bold tracking-tight leading-none flex items-center justify-center gap-2">
              {niceDate}
              {tag && <span className="text-[10px] font-bold uppercase tracking-widest bg-zinc-900 text-white rounded px-1.5 py-0.5">{tag}</span>}
            </div>
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftDay(1)} data-testid="coverage-next-day"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-9" onClick={() => setDate(yesterdayStr())} data-testid="coverage-yesterday">Yesterday</Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setDate(todayStr())} data-testid="coverage-today">Today</Button>
          <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-9 w-[150px]" data-testid="coverage-date-input" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : !data ? (
        <p className="text-sm text-zinc-400 py-10 text-center">Couldn't load coverage for this day.</p>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {kpis.map(({ label, value, cls, num, icon: Icon }) => (
              <div key={label} className={`border rounded-md p-3 ${cls}`} data-testid={`coverage-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold opacity-80">
                  <Icon className="w-3.5 h-3.5" /> {label}
                </div>
                <div className={`text-3xl font-bold font-display mt-1 ${num}`}>{value}</div>
              </div>
            ))}
          </div>

          {/* Shift cards */}
          {data.shifts.length === 0 ? (
            <div className="bg-white border border-dashed border-zinc-300 rounded-md py-12 text-center text-zinc-500" data-testid="coverage-empty">
              No shifts assigned for this day. Use the <span className="font-semibold">Roster builder</span> tab to allocate.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {data.shifts.map((s) => {
                const c = colorOf(s.color);
                const Icon = shiftIcon(s.start_time);
                return (
                  <button
                    key={s.type}
                    onClick={() => setDrill({ name: s.name, color: s.color, total: s.total, by_department: s.by_department, time: `${s.start_time}–${s.end_time}` })}
                    className={`text-left border ${c.border} ${c.bg} rounded-lg p-4 hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 ${c.ring}`}
                    data-testid={`coverage-card-${s.type}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-9 h-9 rounded-md ${c.solid} text-white flex items-center justify-center`}><Icon className="w-4.5 h-4.5" /></span>
                        <div>
                          <div className={`font-display text-lg font-bold tracking-tight ${c.text}`}>{s.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{s.start_time}–{s.end_time}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-3xl font-black font-display leading-none ${c.text}`}>{s.total}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">on shift</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {s.by_department.map((d) => (
                        <span key={d.department} className="inline-flex items-center gap-1.5 bg-white/70 border border-zinc-200 rounded-full px-2 py-0.5 text-xs">
                          <span className="font-semibold text-zinc-800">{d.department}</span>
                          <span className={`font-bold ${c.text}`}>{d.count}</span>
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-zinc-400 mt-3 font-semibold uppercase tracking-widest">
                      View team <ArrowRight className="w-3 h-3" />
                    </div>
                  </button>
                );
              })}

              {/* Unassigned card */}
              {data.unassigned.total > 0 && (
                <button
                  onClick={() => setDrill({ name: "Unassigned", color: "zinc", total: data.unassigned.total, by_department: data.unassigned.by_department, unassigned: true })}
                  className="text-left border border-zinc-200 bg-zinc-50 rounded-lg p-4 hover:shadow-md hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 ring-zinc-400"
                  data-testid="coverage-card-unassigned"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-9 h-9 rounded-md bg-zinc-400 text-white flex items-center justify-center"><UserX className="w-4.5 h-4.5" /></span>
                      <div>
                        <div className="font-display text-lg font-bold tracking-tight text-zinc-700">Unassigned</div>
                        <div className="text-xs text-zinc-500">No shift this day</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-black font-display leading-none text-zinc-700">{data.unassigned.total}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">staff</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {data.unassigned.by_department.map((d) => (
                      <span key={d.department} className="inline-flex items-center gap-1.5 bg-white border border-zinc-200 rounded-full px-2 py-0.5 text-xs">
                        <span className="font-semibold text-zinc-800">{d.department}</span>
                        <span className="font-bold text-zinc-600">{d.count}</span>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 text-[11px] text-zinc-400 mt-3 font-semibold uppercase tracking-widest">
                    View staff <ArrowRight className="w-3 h-3" />
                  </div>
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Drill-down dialog */}
      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {drill && !drill.unassigned && <span className={`w-3 h-3 rounded-full ${colorOf(drill.color).solid}`} />}
              {drill?.name}
              <span className="text-sm font-normal text-zinc-400">
                · {drill?.total} {drill?.unassigned ? "staff" : "on shift"}{drill?.time ? ` · ${drill.time}` : ""}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-auto pr-1" data-testid="coverage-drilldown">
            {drill?.by_department?.map((d) => (
              <div key={d.department}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="label-overline">{d.department}</span>
                  <span className="text-xs font-bold text-zinc-500">{d.count}</span>
                </div>
                <div className="space-y-1.5">
                  {d.members.map((m) => (
                    <div key={m.id} className="flex items-center justify-between bg-zinc-50 border border-zinc-100 rounded-md px-3 py-2" data-testid={`drill-member-${m.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{m.name}</div>
                        <div className="text-[11px] text-zinc-500 capitalize">{roleLabel(m.role)}{m.note ? ` · ${m.note}` : ""}</div>
                      </div>
                      {m.start_time && (
                        <span className="text-[11px] font-mono text-zinc-500 shrink-0 ml-2">{m.start_time}–{m.end_time}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
